import React, { Suspense } from 'react'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import CacheBuster from 'react-cache-buster'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { BrowserRouter } from 'react-router-dom'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'

import packageJson from '../../package.json'

import { logError, CircleSpinner, ErrorFallbackPage } from 'shared'
import { UseAuthProvider } from 'shared/contexts/auth'
import appConfig from 'app.config'

import './App.scss'
import RootRoute from './containers/Home/RootRoute'

import { library } from '@fortawesome/fontawesome-svg-core'
import { fab } from '@fortawesome/free-brands-svg-icons'
import {
	faTrashAlt,
	faChevronDown,
	faChevronUp,
	faExclamationTriangle,
	faSearchDollar,
	faSortUp,
	faSortDown,
	faClock,
	faPaperPlane,
	faTimes,
} from '@fortawesome/free-solid-svg-icons'

library.add(
	fab,
	faTrashAlt,
	faChevronDown,
	faChevronUp,
	faExclamationTriangle,
	faSearchDollar,
	faSortUp,
	faSortDown,
	faClock,
	faPaperPlane,
	faTimes,
)

const MetaTags = ({ isEnvProd }: any) => {
	const description = document.querySelector('meta[name="description"]')
	if (description) {
		description.remove()
	}
	const getTitle = () => {
		switch (appConfig.country) {
			case 'at':
				return 'Meine TF Bank'
			case 'de':
				return 'Meine TF Bank'
			case 'no':
				return 'TF Bank Minside'
			default:
				return 'My Pages | TF Bank'
		}
	}

	const getDesc = () => {
		switch (appConfig.country) {
			case 'at':
				return 'Meine TF Bank'
			case 'de':
				return 'Meine TF Bank'
			case 'no':
				return 'TF Bank Minside'
			default:
				return 'My Pages TF Bank AB'
		}
	}

	const getMeta = () => {
		if (isEnvProd) {
			return [
				{
					name: 'description',
					content: `${getDesc()}`,
				},
			]
		} else {
			return [
				{
					name: 'description',
					content: `${getDesc()}`,
				},
				{
					name: 'robots',
					content: 'none',
				},
			]
		}
	}

	return <Helmet title={getTitle()} meta={getMeta()} />
}

function errorFallback({ error, resetErrorBoundary }: FallbackProps) {
	return (
		<ErrorFallbackPage
			error={error}
			resetErrorBoundary={resetErrorBoundary}
		/>
	)
}

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: 1,
		},
	},
})

const App: React.FC = () => {
	const isEnvProd = process.env.REACT_APP_ENVIRONMENT === 'production'
	const version = packageJson.version

	return (
		<QueryClientProvider client={queryClient}>
			<ReactQueryDevtools />
			<HelmetProvider>
				<BrowserRouter>
					<div className={`App ${appConfig.country}`}>
						<Suspense fallback={<CircleSpinner />}>
							<ErrorBoundary
								fallbackRender={errorFallback}
								onError={(error) => logError(error)}>
								<CacheBuster
									currentVersion={version}
									isEnabled={true} //If false, the library is disabled.
									isVerboseMode={false} //If true, the library writes verbose logs to console.
									loadingComponent={<CircleSpinner />} //If not pass, nothing appears at the time of new version check.
								>
									<UseAuthProvider>
										<MetaTags isEnvProd={isEnvProd} />
										<RootRoute />
									</UseAuthProvider>
								</CacheBuster>
							</ErrorBoundary>
						</Suspense>
					</div>
				</BrowserRouter>
			</HelmetProvider>
		</QueryClientProvider>
	)
}

export default App
